/**
 * React Query hook for dashboard data
 * Provides real-time dashboard metrics with caching and automatic refetching
 */

import { useQuery } from '@tanstack/react-query';
import { dashboardApi, type DashboardMetrics, type ActivityEvent } from '../services/dashboardApi';

/**
 * Query key for dashboard metrics
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  metrics: () => [...dashboardKeys.all, 'metrics'] as const,
  activity: () => [...dashboardKeys.all, 'activity'] as const,
};

/**
 * Hook to fetch dashboard metrics
 * Automatically caches data and handles loading/error states
 */
export function useDashboard() {
  return useQuery<DashboardMetrics, Error>({
    queryKey: dashboardKeys.metrics(),
    queryFn: dashboardApi.getMetrics,
    staleTime: 30_000, // Consider data fresh for 30 seconds
    refetchInterval: 60_000, // Refetch every minute for live updates
  });
}

export function useDashboardActivity(limit = 25) {
  return useQuery<{ items: ActivityEvent[] }, Error>({
    queryKey: [...dashboardKeys.activity(), limit],
    queryFn: () => dashboardApi.getActivity(limit),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
